import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase.js";

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const cache = new Map();

// Cache key generator
const getCacheKey = (params) => {
  const { language, category, cursor, pageSize } = params;
  return `news:${language}:${category || 'all'}:${cursor || 'first'}:${pageSize}`;
};

// Cache helper functions
const getCached = (key) => {
  const cached = cache.get(key);
  if (!cached) return null;
  
  const isExpired = Date.now() - cached.timestamp > CACHE_TTL;
  if (isExpired) {
    cache.delete(key);
    return null;
  }
  
  return cached.data;
};

const setCache = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now(),
  });
  
  // Cleanup old entries if cache gets too large
  if (cache.size > 50) {
    const oldestKeys = Array.from(cache.keys()).slice(0, 10);
    oldestKeys.forEach(k => cache.delete(k));
  }
};

// Clear all cache
export const clearNewsCache = () => {
  cache.clear();
};

// Clear cache for specific language/category
export const clearNewsCacheFor = (language, category = null) => {
  const keysToDelete = [];
  for (const key of cache.keys()) {
    if (key.includes(`news:${language}`) && (!category || key.includes(category))) {
      keysToDelete.push(key);
    }
  }
  keysToDelete.forEach(key => cache.delete(key));
};

// Build Firestore query
const buildQuery = ({ language, category, cursor, pageSize = 12 }) => {
  const filters = [where("language", "==", language)];
  
  if (category && category !== "__all") {
    filters.push(where("category", "==", category));
  }
  
  let q = query(
    collection(db, "news"),
    ...filters,
    orderBy("date", "desc"),
    limit(pageSize)
  );
  
  if (cursor) {
    q = query(q, startAfter(cursor));
  }
  
  return q;
};

// Fetch news with caching
export const fetchNews = async ({
  language,
  category = null,
  cursor = null,
  pageSize = 12,
  useCache = true,
}) => {
  const cacheKey = getCacheKey({ language, category, cursor, pageSize });
  
  // Check cache first
  if (useCache) {
    const cached = getCached(cacheKey);
    if (cached) {
      return cached;
    }
  }
  
  try {
    const q = buildQuery({ language, category, cursor, pageSize });
    const snapshot = await getDocs(q);
    
    const items = [];
    snapshot.forEach((doc) => {
      items.push({ id: doc.id, ...doc.data() });
    });
    
    const result = {
      items,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: items.length === pageSize,
      total: items.length,
    };
    
    // Cache the result
    if (useCache) {
      setCache(cacheKey, result);
    }
    
    return result;
  } catch (error) {
    console.error("newsService: Error fetching news:", error);
    throw error;
  }
};

// Fetch categories for a language
export const fetchCategories = async (language) => {
  const cacheKey = `categories:${language}`;
  
  // Check cache
  const cached = getCached(cacheKey);
  if (cached) return cached;
  
  try {
    // Fetch a sample of articles to extract categories
    const q = query(
      collection(db, "news"),
      where("language", "==", language),
      limit(100)
    );
    
    const snapshot = await getDocs(q);
    const categories = new Set(["__all"]);
    
    snapshot.forEach((doc) => {
      const category = doc.data().category;
      if (category) {
        categories.add(category);
      }
    });
    
    const result = Array.from(categories);
    setCache(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error("newsService: Error fetching categories:", error);
    return ["__all"];
  }
};

// Prefetch next page in background
export const prefetchNextPage = async ({
  language,
  category,
  lastDoc,
  pageSize = 12,
}) => {
  if (!lastDoc) return;
  
  try {
    // Prefetch without waiting
    setTimeout(() => {
      fetchNews({
        language,
        category,
        cursor: lastDoc,
        pageSize: Math.min(pageSize, 6), // Prefetch smaller batch
        useCache: true,
      }).catch(() => {
        // Silent fail for prefetch
      });
    }, 100);
  } catch (error) {
    // Silent fail
  }
};

// Get single article by ID (with caching)
export const fetchArticleById = async (articleId) => {
  const cacheKey = `article:${articleId}`;
  
  const cached = getCached(cacheKey);
  if (cached) return cached;
  
  try {
    const docRef = doc(db, "news", articleId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const result = { id: docSnap.id, ...docSnap.data() };
    setCache(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error("newsService: Error fetching article:", error);
    throw error;
  }
};

// Batch prefetch multiple articles
export const batchPrefetchArticles = async (articleIds) => {
  const promises = articleIds.map(id => 
    fetchArticleById(id).catch(() => null)
  );
  
  return Promise.allSettled(promises);
};

export default {
  fetchNews,
  fetchCategories,
  prefetchNextPage,
  fetchArticleById,
  batchPrefetchArticles,
  clearNewsCache,
  clearNewsCacheFor,
};
